import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { message } from 'antd'
import { useHotkeys } from 'react-hotkeys-hook'
import CaseListPanel from '../../components/Annotation/CaseListPanel'
import QuestionPanelV2 from '../../components/Annotation/QuestionPanelV2'
import ReplyPanelV2 from '../../components/Annotation/ReplyPanelV2'
import DocPreviewModal from '../../components/Annotation/DocPreviewModal'
import type { BiddingCase, AnnotationData } from '../../types/index'
import { mockCases, mockAnnotationData } from '../../services/mockData'

const AnnotationWorkspace = () => {
  const { caseId } = useParams()
  const [selectedCase, setSelectedCase] = useState<BiddingCase | null>(null)
  const [annotationData, setAnnotationData] = useState<AnnotationData | null>(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewDoc, setPreviewDoc] = useState<{ type: 'question' | 'reply'; url: string } | null>(null)
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null)
  const [selectedReplyId, setSelectedReplyId] = useState<string | null>(null)

  // 加载案件数据
  useEffect(() => {
    if (caseId) {
      const caseData = mockCases.find(c => c.caseId === caseId)
      if (caseData) {
        setSelectedCase(caseData)
        // 加载标注数据
        const annotation = mockAnnotationData[caseId]
        if (annotation) {
          setAnnotationData(annotation)
        }
      }
    }
  }, [caseId])

  // 快捷键
  useHotkeys('ctrl+s', (e) => {
    e.preventDefault()
    handleSave()
  }, [annotationData])

  useHotkeys('ctrl+up', () => {
    // 切换到上一个事项
  })

  useHotkeys('ctrl+down', () => {
    // 切换到下一个事项
  })

  const handleSave = () => {
    message.success('保存成功')
  }

  const handleSubmit = () => {
    message.success('提交审核成功')
  }

  const handlePreviewDoc = (type: 'question' | 'reply') => {
    setPreviewDoc({
      type,
      url: type === 'question' 
        ? selectedCase?.questionDoc?.filePath || ''
        : selectedCase?.replyDoc?.filePath || ''
    })
    setPreviewVisible(true)
  }

  const handleQuestionSelect = (questionId: string) => {
    setSelectedQuestionId(questionId)
    // 查找关联的答复
    const relatedReply = annotationData?.replyDetails.find(
      r => r.related_inquiry_item_id === questionId
    )
    if (relatedReply) {
      setSelectedReplyId(relatedReply.item_id)
    }
  }

  const handleReplySelect = (replyId: string) => {
    setSelectedReplyId(replyId)
    // 查找关联的质疑
    const reply = annotationData?.replyDetails.find(r => r.item_id === replyId)
    if (reply) {
      setSelectedQuestionId(reply.related_inquiry_item_id)
    }
  }

  return (
    <div className="h-[calc(100vh-112px)] flex flex-col">
      {/* 顶部工具栏 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">
            {selectedCase ? selectedCase.caseId : '请选择案件'}
          </h2>
          {selectedCase && (
            <span className="text-gray-500 text-sm">
              {selectedCase.projectName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            快捷键: Ctrl+S 保存 | Ctrl+↑/↓ 切换事项
          </span>
        </div>
      </div>

      {/* 三栏式布局 */}
      <div className="flex-1 grid grid-cols-[280px_1fr_1fr] gap-4 min-h-0">
        {/* 左栏：案件列表 */}
        <CaseListPanel
          cases={mockCases}
          selectedCaseId={selectedCase?.caseId}
          onSelectCase={(caseData) => {
            setSelectedCase(caseData)
            const annotation = mockAnnotationData[caseData.caseId]
            if (annotation) {
              setAnnotationData(annotation)
            }
          }}
        />

        {/* 中栏：质疑函 */}
        <QuestionPanelV2
          caseData={selectedCase}
          annotationData={annotationData}
          selectedQuestionId={selectedQuestionId}
          onSelectQuestion={handleQuestionSelect}
          onPreviewDoc={() => handlePreviewDoc('question')}
          onUpdateAnnotation={(data) => setAnnotationData(data)}
        />

        {/* 右栏：答复函 */}
        <ReplyPanelV2
          caseData={selectedCase}
          annotationData={annotationData}
          selectedReplyId={selectedReplyId}
          onSelectReply={handleReplySelect}
          onPreviewDoc={() => handlePreviewDoc('reply')}
          onUpdateAnnotation={(data) => setAnnotationData(data)}
        />
      </div>

      {/* 文档预览弹窗 */}
      <DocPreviewModal
        visible={previewVisible}
        docType={previewDoc?.type}
        docUrl={previewDoc?.url}
        onClose={() => setPreviewVisible(false)}
      />
    </div>
  )
}

export default AnnotationWorkspace
